export type BaseEvent = {
    readonly name: string
}

export type EventMeta = {
    authFlowId?: string | undefined
    recoveryFlowId?: string | undefined
    access: "private" | "public"
}

export type EventWithMeta<EventT extends string> = {
    name: EventT
    payload: string | number | boolean | null | undefined
    meta: EventMeta
}

export type EventHandler<T extends BaseEvent> = (event: T) => void
export type Middleware<T extends BaseEvent> = (event: T, cancel: () => void) => T | void
export interface EventEmitter<EventT extends BaseEvent> {
    on(eventName: EventT["name"], handler: EventHandler<EventT>): void
    off(eventName: EventT["name"], handler: EventHandler<EventT>): void
    emit(event: EventT): void
    use(middleware: Middleware<EventT>): void
}

export function createEventEmitter<EventT extends BaseEvent>(): EventEmitter<EventT> {
    const eventHandlers: Record<string, Set<EventHandler<EventT>>> = {}
    const middlewares: Set<Middleware<EventT>> = new Set()

    function on(eventName: EventT["name"], handler: EventHandler<EventT>) {
        if (!eventHandlers[eventName]) {
            eventHandlers[eventName] = new Set<EventHandler<EventT>>()
        }
        eventHandlers[eventName].add(handler)
    }

    function off(eventName: EventT["name"], handler: EventHandler<EventT>) {
        const handlers = eventHandlers[eventName]
        if (handlers) {
            handlers.delete(handler)
        }
    }

    function emit(event: EventT) {
        const handlers = eventHandlers[event.name]

        if (!handlers || !handlers.size) {
            return
        }

        let cancelled = false

        const eventWithMiddleware =
            middlewares.size > 0
                ? [...middlewares].reduce((eventAggregate, middleware) => {
                      if (cancelled) {
                          return eventAggregate
                      }

                      return (
                          middleware(eventAggregate, () => {
                              cancelled = true
                          }) || eventAggregate
                      )
                  }, event)
                : event

        if (cancelled) {
            return
        }

        handlers.forEach((handler) => handler(eventWithMiddleware))
    }

    /**
     * Each middleware is called in the order it was added.
     * It must either return an event (potentially modified) or call the cancel function to stop the event from being emitted.
     */
    function use(middleware: Middleware<EventT>) {
        middlewares.add(middleware)
    }

    return { on, off, emit, use }
}
